<template>
  <div>
    <nx-dialog
      :value="dialog"
      :icon="icon"
      :title="translate(label)"
      min-height="300"
      lazy
      max-width="1200px"
      @input="close"
    >
      <div v-if="description">
        <p v-html="translate(description)" />
      </div>
      <nx-form-generator-scope
        v-if="fieldsConfig"
        ref="form"
        :config="fieldsConfig"
        :show-tabs="false"
        :value="fields"
        @input="updateField"
        @submit="execute"
        @form="form = $event"
      />
      <template #actions>
        <v-spacer />
        <v-btn
          flat
          :disabled="loading"
          @click="close"
          v-text="$t('message.common.cancel')"
        />
        <v-btn
          flat
          :loading="loading"
          color="accent"
          :disabled="loading || !isValid"
          @click="execute"
          v-text="$t('message.common.accept')"
        />
      </template>
    </nx-dialog>
    <nx-dialog-confirm
      ref="confirm"
      :text="translate(confirmMessage)"
      :title="confirmTitleText"
      :accept-text="confirmButtonText"
      :cancel-text="$t('message.common.cancel')"
      accept-color="primary"
      cancel-color="primary"
    />
  </div>
</template>

<script>
import NxDialog from '@/components/dialogs/Dialog';
import isEmpty from 'lodash/isEmpty';
import $http from '@/api';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import { formatSortParams } from '@/modules/searcher/api';

export default {
  components: {
    NxFormGeneratorScope: () => import('@/modules/form/FormScopeGenerator'),
    NxDialogConfirm,
    NxDialog,
  },
  props: {
    entity: {
      type: String,
      required: true,
    },
    entityIds: {
      type: Array,
      default: () => ([]),
    },
    filters: {
      type: Object,
      required: true,
    },
    sort: {
      type: Object,
      required: true,
    },
    useSearchConfig: {
      type: Boolean,
    },
  },
  data() {
    return {
      action: {},
      dialog: false,
      loading: false,
      form: {},
      fields: {},
    };
  },
  computed: {
    type() {
      return this.action.type;
    },
    endpoint() {
      return this.action.endpoint;
    },
    icon() {
      return this.action.icon;
    },
    label() {
      return this.action.label;
    },
    description() {
      return this.action.description;
    },
    confirmMessage() {
      return this.action.confirmMessage;
    },
    confirmTitleText() {
      const translation = `message.searcher.actions.${this.type}.confirmTitle`;
      return this.$te(translation) ? this.$t(translation) : this.$t('message.list.actions.confirm');
    },
    confirmButtonText() {
      const translation = `message.searcher.actions.${this.type}.confirmButton`;
      return this.$te(translation) ? this.$t(translation) : this.$t('message.common.confirm');
    },
    fieldsConfig() {
      const { fields } = this.action;
      if (isEmpty(fields)) {
        return null;
      }
      return {
        fields,
      };
    },
    searchConfig() {
      return {
        ...this.filters,
        sort: formatSortParams(this.sort),
      };
    },
    isValid() {
      if (!this.fieldsConfig) {
        return true;
      }
      return this.form.valid !== false;
    },
    payload() {
      const payload = {
        type: this.type,
        entity: this.entity,
        context: this.$store.getters.context,
      };
      if (!isEmpty(this.fields)) {
        Object.assign(payload, this.fields);
        Object.assign(payload, { fields: this.fields });
      }
      if (this.useSearchConfig) {
        payload.searchConfig = this.searchConfig;
      } else {
        payload.entityIds = this.entityIds;
      }
      return payload;
    },
  },
  watch: {
    dialog(active) {
      if (active) {
        this.fields = {};
      }
    },
  },
  methods: {
    translate(text) {
      if (isEmpty(text)) {
        return null;
      }
      const translationCandidates = [
        `message.${text}`,
        text,
      ];
      const translation = translationCandidates.find(candidate => this.$te(candidate));
      if (translation) {
        return this.$t(translation);
      }
      return text;
    },
    updateField(field, value) {
      this.$set(this.fields, field, value);
    },
    open(action) {
      this.action = action;
      if (isEmpty(this.action.fields)) {
        this.execute();
      } else {
        this.dialog = true;
      }
    },
    async userConfirmAction() {
      if (!this.confirmMessage) {
        return Promise.resolve(true);
      }
      return this.$refs.confirm.open();
    },
    async execute() {
      try {
        if (!this.isValid || this.loading) {
          return;
        }
        this.loading = true;
        const confirm = await this.userConfirmAction();
        if (!confirm) {
          this.close();
          return;
        }
        await $http.post(this.endpoint, this.payload);
        this.$emit('success');
      } catch (e) {
        console.log(e);
        this.toast.error('message.error.serverError');
      } finally {
        this.loading = false;
        this.close();
      }
    },
    close() {
      this.action = {};
      this.form = {};
      this.fields = {};
      this.dialog = false;
      this.$emit('close');
    },
  },
};
</Script>
